import { useCallback, useEffect, useState } from 'react';
import { ApiError, dialApiFetch, isDialApiConfigured } from '../../api/client';
import type {
  DispositionPayload,
  DispositionResponse,
  QueueRow,
  QueueStatusResponse,
} from '../../api/types';

export interface QueueState {
  configured: boolean;
  pending: number;
  failedRetry: number;
  failedTerminal: number;
  rows: readonly QueueRow[];
  unsynced: boolean;
  lastError: string | null;
}

const EMPTY: QueueState = {
  configured: false,
  pending: 0,
  failedRetry: 0,
  failedTerminal: 0,
  rows: [],
  unsynced: false,
  lastError: null,
};

const POLL_MS = 5_000;

export interface WriteQueue {
  state: QueueState;
  postDisposition: (payload: DispositionPayload) => Promise<void>;
  retryAllFailed: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useWriteQueue(active: boolean): WriteQueue {
  const [state, setState] = useState<QueueState>({
    ...EMPTY,
    configured: isDialApiConfigured,
  });

  const refresh = useCallback(async () => {
    if (!isDialApiConfigured) return;
    try {
      const data = await dialApiFetch<QueueStatusResponse>('/v1/sprints/queue/status');
      setState((prev) => ({
        ...prev,
        configured: true,
        pending: data.counts.pending,
        failedRetry: data.counts.failed_retry,
        failedTerminal: data.counts.failed_terminal,
        rows: data.rows,
        unsynced:
          data.counts.pending +
            data.counts.failed_retry +
            data.counts.failed_terminal +
            data.counts.syncing >
          0,
        lastError: null,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        lastError: err instanceof ApiError ? `status_${err.status}` : 'unknown',
      }));
    }
  }, []);

  // Poll while the runner is active. Stops when the user exits (e.g. summary →
  // close).
  useEffect(() => {
    if (!active || !isDialApiConfigured) return undefined;
    void refresh();
    const id = setInterval(() => {
      void refresh();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [active, refresh]);

  const postDisposition = useCallback(
    async (payload: DispositionPayload) => {
      if (!isDialApiConfigured) {
        // Demo fallback when no dial-api is reachable from the browser.
        // Still log so the developer console matches the real flow.
        console.log('[sprint] disposition (no api):', payload);
        return;
      }
      try {
        await dialApiFetch<DispositionResponse>('/v1/sprints/dispositions', {
          method: 'POST',
          body: payload,
        });
        void refresh();
      } catch (err) {
        setState((prev) => ({
          ...prev,
          lastError: err instanceof ApiError ? `post_${err.status}` : 'post_unknown',
        }));
        // Re-throw so the runner can keep the local record visible to the user
        // even if persistence is broken — they decide whether to retry.
        throw err;
      }
    },
    [refresh],
  );

  const retryAllFailed = useCallback(async () => {
    if (!isDialApiConfigured) return;
    try {
      await dialApiFetch<{ reset: number }>('/v1/sprints/queue/retry-failed', {
        method: 'POST',
      });
      void refresh();
    } catch (err) {
      setState((prev) => ({
        ...prev,
        lastError: err instanceof ApiError ? `retry_${err.status}` : 'retry_unknown',
      }));
    }
  }, [refresh]);

  return { state, postDisposition, retryAllFailed, refresh };
}
